import React from 'react';
import {
	DEFAULT_JULES_EVENT_CLASS_PRIORITY,
	JULES_EVENT_CLASS_LABELS,
	normalizeEventClassPriority,
	parseJulesTime,
} from './entitlement';
import { hasAstroLocation } from '../astroUtils';

/**
 * Jules settings block for SystemEditor (entitled users only).
 */
export default function JulesSystemSettings({ values, onChange, previewSystem }) {
	const priority = normalizeEventClassPriority(values.julesEventClassPriority);
	const aiHelpEnabled = values.aiHelpEnabled === true;
	const letJulesDecide = values.letJulesDecide === true;
	const hasLocation = hasAstroLocation(previewSystem);
	const onOk = !!parseJulesTime(values.julesOnTime);
	const offOk = !!parseJulesTime(values.julesOffTime);

	const movePriority = (index, direction) => {
		const next = [...priority];
		const target = index + direction;
		if (target < 0 || target >= next.length) return;
		[next[index], next[target]] = [next[target], next[index]];
		onChange({ julesEventClassPriority: next });
	};

	return (
		<div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
			<div>
				<h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Jules</h4>
				<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
					AI event suggestions and optional autonomous daily lighting.
				</p>
			</div>

			<label className="flex items-start gap-2 text-sm">
				<input
					type="checkbox"
					className="mt-0.5"
					checked={aiHelpEnabled}
					onChange={(e) => {
						const checked = e.target.checked;
						onChange({
							aiHelpEnabled: checked,
							...(checked ? {} : { letJulesDecide: false }),
						});
					}}
				/>
				<span>
					<span className="font-medium">Enable Jules</span>
					<span className="block text-xs text-gray-500 dark:text-gray-400">
						Show suggestions in Scene and Schedule editors.
					</span>
				</span>
			</label>

			<label className={`flex items-start gap-2 text-sm ${!aiHelpEnabled ? 'opacity-50' : ''}`}>
				<input
					type="checkbox"
					className="mt-0.5"
					checked={letJulesDecide}
					disabled={!aiHelpEnabled}
					onChange={(e) => onChange({ letJulesDecide: e.target.checked })}
				/>
				<span>
					<span className="font-medium">Let Jules decide</span>
					<span className="block text-xs text-gray-500 dark:text-gray-400">
						Apply a daily event pick automatically during the on window.
					</span>
				</span>
			</label>

			{letJulesDecide && aiHelpEnabled && (!onOk || !offOk) && (
				<p className="text-xs text-amber-600 dark:text-amber-400">
					Set valid on and off times (HH:mm) for autonomous mode.
				</p>
			)}

			{!hasLocation && (
				<p className="text-xs text-amber-600 dark:text-amber-400">
					Latitude and longitude are required for local event discovery (same as astronomical schedules).
				</p>
			)}

			<div className="grid grid-cols-2 gap-3">
				<div>
					<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Jules on time</label>
					<input
						type="time"
						className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full text-sm"
						value={values.julesOnTime || '18:00'}
						onChange={(e) => onChange({ julesOnTime: e.target.value })}
						disabled={!aiHelpEnabled}
					/>
				</div>
				<div>
					<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Jules off time</label>
					<input
						type="time"
						className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full text-sm"
						value={values.julesOffTime || '23:00'}
						onChange={(e) => onChange({ julesOffTime: e.target.value })}
						disabled={!aiHelpEnabled}
					/>
				</div>
			</div>
			<p className="text-xs text-gray-500 dark:text-gray-400 -mt-1">
				Interpreted in the system timezone. Off only blacks out if Jules still owns the current look.
			</p>

			<div>
				<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1.5">
					Event class priority
				</label>
				<p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
					Higher in the list wins when multiple popular events exist that day.
				</p>
				<div className="space-y-1">
					{priority.map((id, index) => (
						<div
							key={id}
							className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-2 py-1.5 bg-gray-50 dark:bg-gray-800"
						>
							<span className="text-xs text-gray-400 w-4 tabular-nums">{index + 1}</span>
							<span className="flex-1 text-sm truncate">
								{JULES_EVENT_CLASS_LABELS[id] || id}
							</span>
							<button
								type="button"
								disabled={!aiHelpEnabled || index === 0}
								onClick={() => movePriority(index, -1)}
								className="px-1.5 py-0.5 text-xs border rounded disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
							>
								↑
							</button>
							<button
								type="button"
								disabled={!aiHelpEnabled || index === priority.length - 1}
								onClick={() => movePriority(index, 1)}
								className="px-1.5 py-0.5 text-xs border rounded disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
							>
								↓
							</button>
						</div>
					))}
				</div>
				{!values.julesEventClassPriority?.length && (
					<p className="text-[10px] text-gray-400 mt-1">
						Defaults: {DEFAULT_JULES_EVENT_CLASS_PRIORITY.map((id) => JULES_EVENT_CLASS_LABELS[id]).join(' → ')}
					</p>
				)}
			</div>
		</div>
	);
}
